import React from 'react';
import { CheckCircle2, Clock, Info, XCircle } from 'lucide-react';
import {
  EarlyPaymentInfo,
  EarlyRequestStatus,
  getEarlyPaymentSettlement,
  NZ_MIN_THRESHOLD,
} from '../../../../utils/nzLiquidacion';
import { BA_TIMEZONE } from '../../../../utils/timezone';

interface NZEarlyPaymentNoticeProps {
  /** En qué quedó la última solicitud de anticipada — ver getEarlyRequestStatus. Sin esta prop (o
   *  `none`) solo se muestra lo abonado. */
  status?: EarlyRequestStatus;
  /** Anticipada registrada del mes (finanzas-gitlab), si la hay. Según la fecha límite de pago de
   *  `paymentMonth` se muestra como "a abonar" o como "abonada". */
  payment?: EarlyPaymentInfo;
  /** 'YYYY-MM' del período de `payment`. Define la fecha límite de pago (día 5 del mes siguiente). */
  paymentMonth?: string;
  /** "Septiembre de 2026": mes en el que se hizo la solicitud, para nombrarlo cuando el aviso se
   *  muestra mientras la psicóloga mira OTRO mes. */
  requestMonthLabel?: string;
}

const formatUsd = (amount: number) =>
  `USD ${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: BA_TIMEZONE });

const formatDate = (date: Date) =>
  date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Avisos de "Tus liquidaciones" sobre la anticipada de un mes: la que finanzas ya registró — "a
 * abonar" hasta su fecha límite de pago, "abonada" después; en ambos casos sus sesiones ya no suman
 * para la franja ni se vuelven a pagar — y la solicitud de la psicóloga según en qué
 * quedó (pendiente / sin efecto por haber alcanzado la franja / vencida sin abonar). Puramente
 * presentacional: el estado lo decide `getEarlyRequestStatus`. No renderiza nada si no hay
 * ni pago ni solicitud que mostrar.
 */
const NZEarlyPaymentNotice: React.FC<NZEarlyPaymentNoticeProps> = ({
  status = { kind: 'none' },
  payment,
  paymentMonth,
  requestMonthLabel,
}) => {
  const hasPayment = !!payment && payment.sessionsPaid > 0;
  // Sin mes no se puede saber la fecha de pago: se muestra como "a abonar" sin prometer una fecha.
  const settlement = paymentMonth ? getEarlyPaymentSettlement(paymentMonth) : null;
  const isPaid = settlement?.status === 'paid';
  if (!hasPayment && status.kind === 'none') return null;

  return (
    <div className="flex flex-col gap-2" data-testid="nz-liquidacion-anticipada-avisos">
      {hasPayment && payment && isPaid && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 flex gap-2"
          data-testid="nz-liquidacion-anticipada-pagada"
        >
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-emerald-900">
            <p className="font-semibold">Anticipada abonada</p>
            <p className="text-emerald-800">
              Te abonamos {payment.sessionsPaid} {payment.sessionsPaid === 1 ? 'sesión' : 'sesiones'} por{' '}
              {formatUsd(payment.amountPaid)}. Esas sesiones ya no suman para tu franja ni se vuelven a liquidar.
            </p>
          </div>
        </div>
      )}

      {hasPayment && payment && !isPaid && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex gap-2"
          data-testid="nz-liquidacion-anticipada-a-abonar"
        >
          <Clock size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Anticipada registrada, pendiente de pago</p>
            <p className="text-amber-800">
              Vamos a abonarte {payment.sessionsPaid} {payment.sessionsPaid === 1 ? 'sesión' : 'sesiones'} por{' '}
              {formatUsd(payment.amountPaid)}, pero todavía no se abonó.{' '}
              {settlement
                ? `Los pagos se procesan hasta el día 5 del mes siguiente (si coincide con un fin de semana o feriado, el primer día hábil posterior): se abona hasta el ${formatDate(settlement.dueDate)}. `
                : 'Los pagos se procesan hasta el día 5 del mes siguiente. '}
              Esas sesiones ya no suman para tu franja ni se vuelven a liquidar.
            </p>
          </div>
        </div>
      )}

      {status.kind === 'dismissed' && (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 flex gap-2"
          data-testid="nz-liquidacion-anticipada-descartada"
        >
          <XCircle size={16} className="text-rose-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-rose-900">
            <p className="font-semibold">Solicitud de anticipada descartada</p>
            <p className="text-rose-800">
              Tu solicitud del {formatDay(status.request.requestedAt)} no corresponde. Motivo: {status.reason}
            </p>
          </div>
        </div>
      )}

      {status.kind === 'pending' && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex gap-2"
          data-testid="nz-liquidacion-anticipada-pendiente"
        >
          <Clock size={16} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Solicitud de anticipada enviada</p>
            <p className="text-amber-800">
              Pediste el {formatDay(status.request.requestedAt)} que se te liquiden {status.request.requestedSessions}{' '}
              sesiones ({formatUsd(status.request.estimatedAmount)}). Todavía no se abonó: te vamos a contactar para
              coordinarlo. Si antes de que cierre el mes llegás a {NZ_MIN_THRESHOLD} sesiones, se liquida por tu
              franja y esta solicitud queda sin efecto.
            </p>
          </div>
        </div>
      )}

      {status.kind === 'superseded' && (
        <div
          className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 flex gap-2"
          data-testid="nz-liquidacion-anticipada-sin-efecto"
        >
          <Info size={16} className="text-violet-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-violet-900">
            <p className="font-semibold">Tu solicitud de anticipada quedó sin efecto</p>
            <p className="text-violet-800">
              Pediste una anticipada el {formatDay(status.request.requestedAt)}, pero{' '}
              {requestMonthLabel ? `en ${requestMonthLabel}` : 'este mes'} alcanzaste tu franja: se liquida según la
              franja, completa. No se abonó ninguna anticipada, así que no hay nada para descontar.
            </p>
          </div>
        </div>
      )}

      {status.kind === 'expired' && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 flex gap-2"
          data-testid="nz-liquidacion-anticipada-vencida"
        >
          <Info size={16} className="text-gray-500 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold">Tu solicitud de anticipada no se abonó</p>
            <p className="text-gray-600">
              Pediste una anticipada el {formatDay(status.request.requestedAt)} y no se llegó a abonar. Esas sesiones
              siguen acumulando para tu próxima liquidación.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default NZEarlyPaymentNotice;
